import React, { useState, useCallback, useRef } from 'react';

import {  createTask, updateTask } from 'actions/tasks';

import { canUser } from 'services/permissions-service';
import { createAlert } from 'services/alert-service';
import { trackEvent } from 'services/events-tracker-service';
import { isDependant } from 'utils/task';

import TaskListItem from 'components/TaskListItem';
import CanUser from 'components/can-user';

import IconSubtask from 'icons/icon-subtask';
import IconTransfer from 'icons/icon-transfer';
import IconArrowDown from 'icons/icon-arrow-down';
import IconArrowUp from 'icons/icon-arrow-up';

const Task = ({ id, name, completed, assignees, due_date, projectId, task_group, total_subtasks, completed_subtasks, completed_on, completed_by, onComplete, onUncomplete, onClick, onTaskGroupSelect }) => {
    const dependantOn = isDependant(id, true);

    return (
        <TaskListItem
            key={id}
            id={id}
            name={name}
            completed={completed}
            assignees={assignees}
            dueDate={due_date}
            taskGroup={task_group}
            projectId={projectId}
            subtasksCount={total_subtasks}
            completedSubtasksCount={completed_subtasks}
            completedOn={completed && completed_on}
            completedBy={completed && completed_by}
            locked={Boolean(!completed && dependantOn)}
            showComplete={onComplete}
            onComplete={onComplete}
            onUncomplete={onUncomplete}
            onTaskGroupSelect={!dependantOn && onTaskGroupSelect}
            onClick={onClick}
        />
    )
}


const MilestoneTasks = ({ id, milestone, taskGroups, openTasks, completedTasks, onTaskClick }) => {
    const [completedVisible, setCompletedVisible] = useState(false);
    const taskCtrl = useRef(null);


    const handleTaskUncomplete = useCallback((taskId, taskGroupId) => {
        updateTask(taskId, {
            completed: false,
            task_group: taskGroupId
        });
    }, []);

    const handleTaskComplete = useCallback(taskId => {
        updateTask(taskId, {
            completed: true,
            completed_on: new Date()
        });
    }, []);

    const handleTaskTaskGroupSelect = useCallback((taskId, task_group, project) => {
        updateTask(taskId, { task_group, project });
    }, []);

    const handleTaskCreate = useCallback(async (taskId, name) => {
        const taskGroup = taskGroups.find(taskGroup => taskGroup.milestone === id) || taskGroups.find(taskGroup => !taskGroup.milestone);

        if (!taskGroup) {
            createAlert('error', `No available task lists in the project`, {
                duration: 5000
            });
            return;
        }

        taskCtrl.current.reset();

        await createTask({
            name,
            milestone: id,
            project: milestone.project,
            task_group: taskGroup.id
        }).then(() => {
            trackEvent('TASK.CREATE', {
                via: 'Milestone Single'
            });
        });

        if (taskCtrl.current) {
            setTimeout(() => {
                taskCtrl.current.beginEdit();
            }, 50);
        }
    }, [taskGroups, id, milestone]);

    const handleCompletedTasksToggle = useCallback(() => {
        if (!completedTasks.length) {
            return;
        }

        setCompletedVisible(!completedVisible);
    }, [completedTasks, completedVisible]);

    return (
        <React.Fragment>
            <div className="section-head section-head-double">
                <div className="head-item">
                    <h4>
                        <span className="title-icon"><IconSubtask /></span>

                        <span className="text">Tasks</span>
                    </h4>
                </div>

                <div className="head-item">
                    <h4>
                        <span className="title-icon"><IconTransfer /></span>

                        <span className="text">Task</span>
                    </h4>
                </div>
            </div>

            <div className="content-panel-subtasks">
                <div className="subtasks">
                    {
                        openTasks.map(task => (
                            <Task
                                key={task.id}
                                id={task.id}
                                name={task.name}
                                completed={task.completed}
                                assignees={task.assignees}
                                due_date={task.due_date}
                                projectId={task.project}
                                task_group={task.task_group}
                                total_subtasks={task.total_subtasks}
                                completed_on={task.completed_on}
                                completed_by={task.completed_by}
                                onUncomplete={canUser('editTask') && handleTaskUncomplete}
                                onComplete={canUser('editTask') && handleTaskComplete}
                                onTaskGroupSelect={canUser('editTask') && handleTaskTaskGroupSelect}
                                onClick={onTaskClick}
                            />
                        ))
                    }

                    <CanUser type="createTask">
                        <TaskListItem
                            key="new"
                            new={true}
                            editing={true}
                            canEdit={true}
                            onNameChange={handleTaskCreate}
                            namePlaceholder="Add a new task..."
                            provideController={taskCtrl}
                        />
                    </CanUser>
                </div>

                {
                    completedTasks.length ? (
                        <div className="subtasks-list-toggle" onClick={handleCompletedTasksToggle}>
                            { completedTasks.length } Completed Task{completedTasks.length === 1 ? '' : 's'}

                            {
                                !completedVisible ? <IconArrowDown /> : <IconArrowUp />
                            }
                        </div>
                    ) : null
                }

                {
                    completedVisible ? (
                        <div className="subtasks">
                            {
                                completedTasks.map(task => (
                                    <Task
                                        key={task.id}
                                        id={task.id}
                                        name={task.name}
                                        completed={task.completed}
                                        assignees={task.assignees}
                                        due_date={task.due_date}
                                        total_subtasks={task.total_subtasks}
                                        completed_on={task.completed_on}
                                        completed_by={task.completed_by}
                                        projectId={task.project}
                                        onClick={onTaskClick}
                                        onUncomplete={canUser('editTask') && handleTaskUncomplete}
                                    />
                                ))
                            }
                        </div>
                    ) : null
                }
            </div>
        </React.Fragment>
    );
}

export default MilestoneTasks;
